import { useState, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Truck, Bell, Download, AlertTriangle, RotateCcw } from "lucide-react";
import {
  PortalPage,
  PortalHero,
  StepIndicator,
  CardWrap,
  InlineError,
  GoldButton,
  PortalGuard,
  PortalUser,
  PORTAL_EMAIL,
  PORTAL_PHONE,
} from "./shared";
import { getCargoShortInfo } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";
import { parseDateOnly } from "@/lib/dateUtils";
import { getCsrfToken } from "@/lib/queryClient";

// ─── Types ─────────────────────────────────────────────────────────────────────
interface FmcsaRecord {
  legal_name?: string;
  dba_name?: string;
  dot_number?: string;
  bus_city?: string;
  bus_state_code?: string;
  email_address?: string;
  record_status?: string;
  common_stat?: string;
  contract_stat?: string;
  broker_stat?: string;
  common_app_pend?: string;
  contract_app_pend?: string;
  broker_app_pend?: string;
  property_chk?: string;
  passenger_chk?: string;
  hhg_chk?: string;
}

interface HelixLetter {
  id: number;
  service_date?: string;
  letter_type?: string;
  authority_type?: string;
  letter_code?: string;
  document_identifier?: string;
  docket_full?: string;
  company_name?: string;
}

interface PortalRates {
  authorityLetter: string;
  archiveLetterRequest: string;
}

interface WatchItem {
  authority_type_code?: string;
  letter_type?: string;
  authority_type?: string;
}

interface PartialFailureItem {
  workOrderId: number;
  workOrderNumber?: string;
  success: boolean;
  error?: string;
  totalAmountCents: number;
  serviceType: string;
  serviceTypeName?: string;
  isWatchlist?: boolean;
}

// Real backend response shape from POST /api/portal/submit-request
interface SubmitApiResponse {
  status: "success" | "partial_failure" | "failed";
  workOrders: number[];  // array of successfully created work order IDs
  workOrderDetails?: PartialFailureItem[];  // full detail for successful work orders
  partialFailures?: PartialFailureItem[];
  logId?: number;
}

interface SubmitState {
  status: "success" | "partial_failure";
  workOrderIds: number[];
  workOrderDetails: PartialFailureItem[];
  partialFailures: PartialFailureItem[];
  carrierName: string;
  docket: string;
  docketPrefix: string;
  docketNumber: string;
  hasWatchlist: boolean;
  selectedLetters: HelixLetter[];
  paymentIntentId: string | null;
  additionalEmail: string;
  logId?: number;
}

type DocketPrefix = "MC" | "FF" | "MX";
type Step =
  | "step1"
  | "step2-loading"
  | "step2-confirm"
  | "step2-error"
  | "step2-unavailable"
  | "step3"
  | "step4"
  | "step5";

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const MAX_DOWNLOAD_ALL = 20;

function isArchiveLetter(serviceDate?: string): boolean {
  if (!serviceDate) return false;
  return new Date(serviceDate).getTime() < Date.now() - ONE_YEAR_MS;
}

function parseDocket(raw: string): { prefix: DocketPrefix | null; number: string } {
  const val = raw.trim().toUpperCase().replace(/-/g, "");
  const prefix = (["FF", "MC", "MX"] as DocketPrefix[]).find((p) => val.startsWith(p)) ?? null;
  const number = prefix ? val.slice(prefix.length) : "";
  return { prefix, number };
}

function isDocketValid(raw: string): boolean {
  const { prefix, number } = parseDocket(raw);
  return !!prefix && /^\d{1,7}$/.test(number);
}

function isFmcsaPending(val?: string): boolean {
  if (!val) return false;
  const upper = val.toUpperCase();
  return upper === "Y" || upper === "YES" || upper === "1";
}

// ─── Portal fetch wrappers — catch 401 and redirect to /portal/login?expired=1 ─
async function portalFetch(url: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(url, { credentials: "include", ...options });
  if (res.status === 401) {
    window.location.href = "/portal/login?expired=1";
    throw new Error("Session expired");
  }
  return res;
}

async function portalApiRequest(method: string, url: string, data?: unknown): Promise<Response> {
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    headers["x-csrf-token"] = await getCsrfToken();
  }
  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  if (res.status === 401) {
    window.location.href = "/portal/login?expired=1";
    throw new Error("Session expired");
  }
  return res;
}

// Renders only the PaymentElement (no submit button — button lives in step3)
function StripePaymentElement({ onComplete }: { onComplete: (complete: boolean) => void }) {
  return <PaymentElement onChange={(e) => onComplete(e.complete)} />;
}

// Mounted inside the shared Elements context during step4 to run confirmPayment
function Step4StripeConfirm({
  onSuccess,
  onError,
}: {
  onSuccess: (piId: string) => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!stripe || !elements || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (error) {
        onError(error.message ?? "Payment failed. Please try again.");
      } else if (
        paymentIntent?.status === "succeeded" ||
        paymentIntent?.status === "requires_capture"
      ) {
        onSuccess(paymentIntent.id);
      } else {
        onError("Payment was not completed. Please try again.");
      }
    })();
  }, [stripe, elements]);

  return null;
}

// ─── Main ──────────────────────────────────────────────────────────────────────
// Inner component — rendered only after PortalGuard confirms service_bureau with companyId
function PortalRequestPageInner({ onLogout, portalUser }: { onLogout: () => void; portalUser: PortalUser }) {
  usePageTitle("Authority Letter Request — DNA Portal");

  // Portal data init
  const [dataLoaded, setDataLoaded] = useState(false);
  const [billingPreference, setBillingPreference] = useState("pay_at_service");
  const [rates, setRates] = useState<PortalRates | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);

  // Wizard state
  const [step, setStep] = useState<Step>("step1");
  const [docketRaw, setDocketRaw] = useState("");
  const [docketPrefix, setDocketPrefix] = useState<DocketPrefix | null>(null);
  const [docketNumber, setDocketNumber] = useState("");
  const [fmcsaRecord, setFmcsaRecord] = useState<FmcsaRecord | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Letters
  const [helixLetters, setHelixLetters] = useState<HelixLetter[]>([]);
  const [helixLoading, setHelixLoading] = useState(false);
  const [selectedLetterIds, setSelectedLetterIds] = useState<Set<number>>(new Set());
  const [letterTypeFilter, setLetterTypeFilter] = useState("all");

  // Watchlist — FMCSA-derived pending application items
  const [selectedPendingIndexes, setSelectedPendingIndexes] = useState<Set<number>>(new Set());
  const [notInListChecked, setNotInListChecked] = useState(false);
  const [nyiLetterType, setNyiLetterType] = useState("");
  const [nyiAuthorityType, setNyiAuthorityType] = useState("");
  const [nyiCargoType, setNyiCargoType] = useState("");

  // Additional contact
  const [additionalEmail, setAdditionalEmail] = useState("");
  const [additionalFirstName, setAdditionalFirstName] = useState("");
  const [additionalLastName, setAdditionalLastName] = useState("");
  const [additionalEmailAutofilled, setAdditionalEmailAutofilled] = useState(false);
  // The requester always receives letters; repeating their email as the additional contact
  // would add a duplicate recipient, so block it before submit.
  const normalizedAdditionalEmail = additionalEmail.trim().toLowerCase();
  const additionalEmailIsRequester =
    normalizedAdditionalEmail !== "" &&
    normalizedAdditionalEmail === (portalUser.email ?? "").trim().toLowerCase();

  // Payment
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [prevPaymentIntentId, setPrevPaymentIntentId] = useState<string | null>(null);
  const [activePaymentIntentId, setActivePaymentIntentId] = useState<string | null>(null);
  const [elementComplete, setElementComplete] = useState(false);

  // Results
  const [submitState, setSubmitState] = useState<SubmitState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [ratesRes, keyRes, companyRes] = await Promise.all([
          portalFetch("/api/portal/rates"),
          portalFetch("/api/portal/stripe-key"),
          portalFetch(`/api/companies/${portalUser.companyId}`),
        ]);
        if (ratesRes.ok) setRates(await ratesRes.json());
        if (keyRes.ok) {
          const { publishableKey } = await keyRes.json();
          if (publishableKey) setStripePromise(loadStripe(publishableKey));
        }
        if (companyRes.ok) {
          const company = await companyRes.json();
          setBillingPreference(company.billingPreference ?? "pay_at_service");
        }
      } catch (err) {
        console.warn("[Portal] Failed to load initial portal data:", err);
      }
      setDataLoaded(true);
    })();
    // portalUser.companyId is stable — set once by PortalGuard
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocketChange = (val: string) => {
    setDocketRaw(val);
    const { prefix, number } = parseDocket(val);
    setDocketPrefix(prefix);
    setDocketNumber(number);
  };

  const lookupCarrier = async () => {
    if (!isDocketValid(docketRaw)) return;
    setLookupError(null);
    setFmcsaRecord(null);
    setStep("step2-loading");
    try {
      const res = await portalApiRequest("POST", "/api/portal/lookup", {
        docketPrefix,
        docketNumber,
      });
      const data = await res.json();

      // Branch 1: FMCSA temporarily unavailable — preserve inputs for retry
      if (
        res.status === 502 ||
        res.status === 504 ||
        data.error === "fmcsa_unavailable" ||
        data.error === "fmcsa_timeout"
      ) {
        setLookupError(
          "FMCSA is temporarily unavailable. Please try again in a moment.",
        );
        setStep("step2-unavailable");
        return;
      }

      // Branch 2: Genuine not-found — reset inputs for re-entry
      if (!res.ok || data.error === "not_found" || !data.legal_name) {
        setLookupError(
          `No carrier found for ${docketPrefix}-${docketNumber}. Please check your docket number and try again.`,
        );
        // Reset all docket inputs so user can enter a different docket
        setDocketRaw("");
        setDocketNumber("");
        setDocketPrefix(null);
        setStep("step2-error");
        return;
      }

      // Branch 3: Success
      setFmcsaRecord(data);
      setStep("step2-confirm");
    } catch (err: unknown) {
      if (err instanceof Error && err.message === "Session expired") return;
      setLookupError("Could not connect to FMCSA. Please try again.");
      setStep("step2-error");
    }
  };

  const confirmCarrier = async () => {
    setHelixLoading(true);
    setHelixLetters([]);
    setSelectedLetterIds(new Set());
    setSelectedPendingIndexes(new Set());
    setNotInListChecked(false);
    setNyiLetterType("");
    setNyiAuthorityType("");
    setNyiCargoType("");
    setAdditionalEmail("");
    setClientSecret(null);
    setPrevPaymentIntentId(null);
    setActivePaymentIntentId(null);
    setStep("step3");

    try {
      // Parallel fetch: Helix letter search + refresh rates
      const [helixRes, ratesRes] = await Promise.all([
        portalApiRequest("POST", "/api/portal/helix-search", {
          docket_prefix: docketPrefix,
          docket_number: docketNumber,
        }),
        portalFetch("/api/portal/rates"),
      ]);
      if (helixRes.ok) {
        const data = await helixRes.json();
        if (Array.isArray(data)) setHelixLetters(data);
      }
      if (ratesRes.ok) {
        setRates(await ratesRes.json());
      }
    } catch (err) {
      console.warn("Helix search error", err);
    } finally {
      setHelixLoading(false);
    }
  };

  const toggleLetter = (id: number) => {
    setSelectedLetterIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Derive combined cargo label from all relevant FMCSA flags for common carrier pending
  const deriveFmcsaCargo = (): string => {
    if (!fmcsaRecord) return "Property";
    const hasPassenger = isFmcsaPending(fmcsaRecord.passenger_chk);
    const hasHhg = isFmcsaPending(fmcsaRecord.hhg_chk);
    const hasProperty = isFmcsaPending(fmcsaRecord.property_chk);
    const parts: string[] = [];
    if (hasPassenger) parts.push("Passengers");
    if (hasHhg) parts.push("Property incl. Household Goods");
    else if (hasProperty) parts.push("Property");
    return parts.length > 0 ? parts.join(" and ") : "Property";
  };

  const fmcsaPendingItems: WatchItem[] = [];
  if (fmcsaRecord) {
    if (isFmcsaPending(fmcsaRecord.common_app_pend)) {
      const cargo = deriveFmcsaCargo();
      fmcsaPendingItems.push({
        letter_type: "Common Carrier Authority",
        authority_type_code: `Common Carrier — ${cargo}`,
      });
    }
    if (isFmcsaPending(fmcsaRecord.contract_app_pend)) {
      const cargo = isFmcsaPending(fmcsaRecord.hhg_chk)
        ? "Property incl. Household Goods"
        : "Property";
      fmcsaPendingItems.push({
        letter_type: "Contract Carrier Authority",
        authority_type_code: `Contract Carrier — ${cargo}`,
      });
    }
    if (isFmcsaPending(fmcsaRecord.broker_app_pend)) {
      const brokerCargo = isFmcsaPending(fmcsaRecord.hhg_chk) ? "Household Goods" : "Freight";
      fmcsaPendingItems.push({
        letter_type: "Broker Authority",
        authority_type_code: `Broker — ${brokerCargo}`,
      });
    }
  }

  const togglePendingItem = (idx: number) => {
    setSelectedPendingIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Build the combined watchlist selection
  const selectedWatchItems: WatchItem[] = [];
  selectedPendingIndexes.forEach((idx) => {
    if (fmcsaPendingItems[idx]) selectedWatchItems.push(fmcsaPendingItems[idx]);
  });
  if (notInListChecked && nyiLetterType) {
    selectedWatchItems.push({
      letter_type: nyiLetterType,
      authority_type_code:
        [nyiAuthorityType, nyiCargoType].filter(Boolean).join(" · ") || undefined,
      authority_type: nyiAuthorityType || undefined,
    });
  }

  const filteredLetters = helixLetters.filter((l) => {
    if (letterTypeFilter === "all") return true;
    const lt = (l.letter_type ?? "").toLowerCase();
    if (letterTypeFilter === "grant") return lt.includes("grant");
    if (letterTypeFilter === "reinstatement") return lt.includes("reinstate");
    if (letterTypeFilter === "name-change") return lt.includes("name");
    if (letterTypeFilter === "transfer") return lt.includes("transfer");
    return true;
  });

  const selectedLetters = helixLetters.filter((l) => selectedLetterIds.has(l.id));
  const recentLetters = selectedLetters.filter((l) => !isArchiveLetter(l.service_date));
  const archiveLetters = selectedLetters.filter((l) => isArchiveLetter(l.service_date));

  const recentRate = parseFloat(rates?.authorityLetter ?? "0");
  const archiveRate = parseFloat(rates?.archiveLetterRequest ?? "0");
  const watchRate = recentRate;
  const recentCost = recentLetters.length * recentRate;
  const archiveCost = archiveLetters.length * archiveRate;
  const watchCost = selectedWatchItems.length * watchRate;
  const totalCost = recentCost + archiveCost + watchCost;
  const totalCents = Math.round(totalCost * 100);
  const hasSelection = selectedLetters.length > 0 || selectedWatchItems.length > 0;
  // If "not in list" is checked but no letter type is picked, block submission
  const nyiIncomplete = notInListChecked && !nyiLetterType;

  const createPaymentIntentMutation = useMutation({
    mutationFn: async (amountCents: number) => {
      const res = await portalApiRequest("POST", "/api/portal/create-payment-intent", {
        amountCents,
        previousPaymentIntentId: prevPaymentIntentId,
      });
      if (!res.ok) throw new Error("Payment intent creation failed");
      return res.json() as Promise<{ clientSecret: string }>;
    },
    onSuccess: (data) => {
      setClientSecret(data.clientSecret);
      const piId = data.clientSecret?.split("_secret_")?.[0] ?? null;
      setPrevPaymentIntentId(piId);
      setActivePaymentIntentId(piId);
    },
  });

  useEffect(() => {
    if (billingPreference !== "pay_at_service" || !stripePromise || totalCents <= 0 || step !== "step3") return;
    // Clear immediately so payment form hides while the new intent is being created
    setClientSecret(null);
    const timer = setTimeout(() => {
      createPaymentIntentMutation.mutate(totalCents);
    }, 600);
    return () => clearTimeout(timer);
  }, [totalCents, billingPreference, step, stripePromise]);

  const submitMutation = useMutation({
    mutationFn: async (paymentIntentId: string | null) => {
      const res = await portalApiRequest("POST", "/api/portal/submit-request", {
        docketPrefix,
        docketNumber,
        dotNumber: fmcsaRecord?.dot_number ?? null,
        selectedLetters,
        selectedWatchItems,
        additionalContactEmail: additionalEmail || null,
        additionalContactFirstName: additionalFirstName || null,
        additionalContactLastName: additionalLastName || null,
        paymentIntentId,
      });
      const data = (await res.json()) as SubmitApiResponse & { message?: string; paymentIntentId?: string };
      if (!res.ok) throw new Error(data.message ?? "There was an issue processing your request. Please contact us and we will resolve it for you as soon as possible.");
      if (data.status === "failed") {
        throw new Error(
          data.message ?? "There was an issue processing your request. Please contact us and we will resolve it for you as soon as possible.",
        );
      }
      return { data, paymentIntentId };
    },
    onSuccess: ({ data, paymentIntentId }) => {
      setSubmitState({
        status: data.status as "success" | "partial_failure",
        workOrderIds: data.workOrders ?? [],
        workOrderDetails: data.workOrderDetails ?? [],
        partialFailures: data.partialFailures ?? [],
        carrierName: fmcsaRecord?.legal_name ?? `${docketPrefix}-${docketNumber}`,
        docket: `${docketPrefix}-${docketNumber}`,
        docketPrefix: docketPrefix ?? "",
        docketNumber,
        hasWatchlist: selectedWatchItems.length > 0,
        selectedLetters,
        paymentIntentId: paymentIntentId ?? activePaymentIntentId,
        additionalEmail,
        logId: data.logId,
      });
      setStep("step5");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (err: Error) => {
      // Return to step3 with inline error — no full error screen
      setErrorMsg(err.message ?? "Submission failed. Please contact support.");
      setStep("step3");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  // For pay_at_service: transition to step4 where Step4StripeConfirm runs confirmPayment()
  const handlePayAtServiceSubmit = () => {
    setStep("step4");
  };

  const handleBatchSubmit = () => {
    setStep("step4");
    submitMutation.mutate(null);
  };

  const downloadLetter = async (helixDocumentId: number) => {
    try {
      const res = await portalFetch(`/api/portal/download-single?helixDocumentId=${helixDocumentId}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `authority-letter-${helixDocumentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Download letter error", err);
    }
  };

  const downloadAll = async () => {
    if (!submitState) return;
    const ids = submitState.selectedLetters
      .slice(0, MAX_DOWNLOAD_ALL)
      .map((l) => l.id)
      .join(",");
    try {
      const res = await portalFetch(`/api/portal/download-all?ids=${ids}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `letters-${submitState.docket}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Download all error", err);
    }
  };

  const renderDocketInput = () => {
    const { prefix } = parseDocket(docketRaw);
    const valid = isDocketValid(docketRaw);
    return (
      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#1B2B4B",
            display: "block",
            marginBottom: 6,
          }}
        >
          Docket Number <span style={{ color: "#C9962A" }}>*</span>
        </label>
        <div style={{ position: "relative" }}>
          {prefix && (
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "#1B2B4B",
                color: "white",
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 8px",
                borderRadius: 5,
                letterSpacing: "0.06em",
                pointerEvents: "none",
              }}
            >
              {prefix}
            </span>
          )}
          <input
            type="text"
            value={docketRaw}
            onChange={(e) => handleDocketChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && isDocketValid(docketRaw) && lookupCarrier()}
            placeholder="e.g. MC-123456 or FF789012"
            style={{
              width: "100%",
              padding: `13px 16px 13px ${prefix ? "80px" : "16px"}`,
              fontSize: 15,
              fontWeight: prefix ? 600 : 400,
              color: "#1a1a1a",
              background: "white",
              border: `1.5px solid ${valid ? "#16a34a" : "#e2ddd6"}`,
              borderRadius: 10,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        {valid && (
          <div style={{ fontSize: 12, color: "#16a34a", marginTop: 5, fontWeight: 600 }}>
            ✓ Parsed: {docketPrefix}-{docketNumber}
          </div>
        )}
        {docketRaw && !valid && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 5 }}>
            {prefix
              ? "Enter the docket number after the prefix (digits only)."
              : "Docket must start with FF, MC, or MX."}
          </div>
        )}
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          Start with FF, MC, or MX — dashes optional. Example:{" "}
          <strong>MC-123456</strong>
        </div>
      </div>
    );
  };

  if (!dataLoaded) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#F7F4EE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader2 size={32} style={{ animation: "spin 0.8s linear infinite", color: "#1B2B4B" }} />
      </div>
    );
  }

  if (step === "step1") {
    return (
      <PortalPage onLogout={onLogout}>
        <PortalHero
          eyebrow="Authority Letter Requests"
          title="Request An Authority Letter"
          subtitle="Enter your MC, FF, or MX docket number to get started."
        />
        <CardWrap maxWidth={560}>
          <StepIndicator current={1} />
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1B2B4B", marginBottom: 6 }}>
            Enter Your Docket Number
          </div>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
            We'll look up your carrier on FMCSA and confirm it's correct before proceeding.
          </p>
          {renderDocketInput()}
          <GoldButton onClick={lookupCarrier} disabled={!isDocketValid(docketRaw)}>
            Look Up Carrier
          </GoldButton>
        </CardWrap>
      </PortalPage>
    );
  }

  if (step === "step2-loading") {
    return (
      <PortalPage onLogout={onLogout}>
        <PortalHero
          eyebrow="Authority Letter Requests"
          title="Request An Authority Letter"
          subtitle="Looking up your carrier with FMCSA…"
        />
        <CardWrap maxWidth={560}>
          <StepIndicator current={2} />
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div
              style={{
                width: 48,
                height: 48,
                border: "3px solid #e2ddd6",
                borderTopColor: "#1B2B4B",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: "#6b7280", fontSize: 14 }}>
              Searching FMCSA for {docketPrefix}-{docketNumber}…
            </p>
          </div>
        </CardWrap>
      </PortalPage>
    );
  }

  if (step === "step2-error") {
    return (
      <PortalPage onLogout={onLogout}>
        <PortalHero eyebrow="Authority Letter Requests" title="Request An Authority Letter" />
        <CardWrap maxWidth={560}>
          <StepIndicator current={2} />
          <InlineError
            message={lookupError ?? "Carrier not found. Please check your docket number."}
          />
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1B2B4B", marginBottom: 16 }}>
            Try Again
          </div>
          {renderDocketInput()}
          <GoldButton onClick={lookupCarrier} disabled={!isDocketValid(docketRaw)}>
            Look Up Carrier
          </GoldButton>
        </CardWrap>
      </PortalPage>
    );
  }

  if (step === "step2-unavailable") {
    return (
      <PortalPage onLogout={onLogout}>
        <PortalHero eyebrow="Authority Letter Requests" title="Request An Authority Letter" />
        <CardWrap maxWidth={560}>
          <StepIndicator current={2} />
          <InlineError
            message={lookupError ?? "FMCSA is temporarily unavailable. Please try again in a moment."}
          />
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1B2B4B", marginBottom: 16 }}>
            {docketPrefix}-{docketNumber}
          </div>
          <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24 }}>
            Your docket number has been preserved. Click below to try again.
          </p>
          <GoldButton onClick={lookupCarrier}>
            Try Again
          </GoldButton>
        </CardWrap>
      </PortalPage>
    );
  }

  if (step === "step2-confirm") {
    const cityState = [fmcsaRecord?.bus_city, fmcsaRecord?.bus_state_code]
      .filter(Boolean)
      .join(", ");
    return (
      <PortalPage onLogout={onLogout}>
        <PortalHero
          eyebrow="Authority Letter Requests"
          title="Confirm Your Carrier"
          subtitle="Please verify this is the correct carrier before continuing."
        />
        <CardWrap maxWidth={620}>
          <StepIndicator current={2} />
          <div
            style={{
              background: "linear-gradient(135deg, #1B2B4B 0%, #243660 100%)",
              borderRadius: 14,
              padding: "32px 36px",
              marginBottom: 28,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(ellipse 60% 80% at 80% 50%, rgba(201,150,42,0.12) 0%, transparent 70%)",
              }}
            />
            <div style={{ position: "relative" }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "#C9962A",
                  marginBottom: 10,
                }}
              >
                ✓ Carrier Found on FMCSA
              </div>
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: "clamp(16px, 2.2vw, 21px)",
                  fontWeight: 700,
                  color: "white",
                  lineHeight: 1.2,
                  marginBottom: 8,
                }}
              >
                {fmcsaRecord?.legal_name}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.85)",
                  letterSpacing: "0.04em",
                  marginBottom: 20,
                }}
              >
                {docketPrefix}-{docketNumber}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "14px 28px" }}>
                {docketPrefix && (
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                    <strong style={{ color: "rgba(255,255,255,0.88)" }}>Entity Type:</strong>{" "}
                    {docketPrefix === "MC"
                      ? "Motor Carrier"
                      : docketPrefix === "FF"
                        ? "Freight Forwarder"
                        : "Mexican Motor Carrier"}
                  </div>
                )}
                {fmcsaRecord?.dba_name && (
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                    <strong style={{ color: "rgba(255,255,255,0.88)" }}>DBA:</strong>{" "}
                    {fmcsaRecord.dba_name}
                  </div>
                )}
                {fmcsaRecord?.dot_number && (
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                    <strong style={{ color: "rgba(255,255,255,0.88)" }}>USDOT:</strong>{" "}
                    {fmcsaRecord.dot_number}
                  </div>
                )}
                {cityState && (
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                    <strong style={{ color: "rgba(255,255,255,0.88)" }}>Location:</strong>{" "}
                    {cityState}
                  </div>
                )}
                {fmcsaRecord?.record_status && (
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                    <strong style={{ color: "rgba(255,255,255,0.88)" }}>Status:</strong>{" "}
                    {fmcsaRecord.record_status}
                  </div>
                )}
              </div>
            </div>
          </div>
          <p style={{ fontSize: 16, fontWeight: 500, color: "#1B2B4B", margin: "24px 0 16px" }}>
            Is this the carrier you're looking for?
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <GoldButton onClick={confirmCarrier} style={{ flex: 1 }}>
              Yes, This Is My Carrier →
            </GoldButton>
            <button
              onClick={() => setStep("step1")}
              style={{
                flex: 1,
                padding: "15px 24px",
                background: "transparent",
                color: "#1B2B4B",
                border: "1.5px solid #e2ddd6",
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Search Again
            </button>
          </div>
        </CardWrap>
      </PortalPage>
    );
  }

  if (step === "step3" || step === "step4") {
    const isPayAtService = billingPreference === "pay_at_service";
    const stripeReady = isPayAtService && !!stripePromise && !!clientSecret;
    const cityState = [fmcsaRecord?.bus_city, fmcsaRecord?.bus_state_code]
      .filter(Boolean)
      .join(", ");
    const showStripeForm = isPayAtService && totalCents > 0 && stripeReady;
    const showStripeLoading = isPayAtService && totalCents > 0 && !clientSecret;
    const submitDisabled =
      !hasSelection ||
      nyiIncomplete ||
      additionalEmailIsRequester ||
      (isPayAtService && (!stripeReady || !elementComplete));

    if (step === "step4" && !isPayAtService) {
      return (
        <PortalPage>
          <PortalHero eyebrow="Authority Letter Requests" title="Request An Authority Letter" />
          <CardWrap maxWidth={560}>
            <StepIndicator current={4} />
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: "3px solid #e2ddd6",
                  borderTopColor: "#1B2B4B",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  margin: "0 auto 20px",
                }}
              />
              <h3
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 22,
                  color: "#1B2B4B",
                  marginBottom: 8,
                }}
              >
                Processing Your Request
              </h3>
              <p style={{ color: "#6b7280", fontSize: 14 }}>
                Creating work orders and sending notifications…
                <br />
                Please don't close or refresh this page.
              </p>
            </div>
          </CardWrap>
        </PortalPage>
      );
    }

    const step3Jsx = step === "step3" || step === "step4" ? (
      <div style={{ display: step === "step4" ? "none" : undefined }}>
      <PortalPage onLogout={onLogout}>
        <PortalHero
          eyebrow="Authority Letter Requests"
          title="Select Your Letters"
          subtitle="Choose from existing letters on file or request monitoring for letters not yet issued."
        />
        <CardWrap maxWidth={780}>
          <StepIndicator current={3} />
          {errorMsg && <InlineError message={errorMsg} />}

          {/* Carrier Banner */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#F7F4EE",
              border: "1.5px solid #e2ddd6",
              borderRadius: 10,
              padding: "14px 18px",
              marginBottom: 28,
            }}
          >
            <Truck size={18} color="#1B2B4B" style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1B2B4B" }}>
                {fmcsaRecord?.legal_name}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {docketPrefix}-{docketNumber}
                {fmcsaRecord?.dot_number && ` · DOT ${fmcsaRecord.dot_number}`}
                {cityState && ` · ${cityState}`}
              </div>
            </div>
          </div>

          {/* Table header + filters */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#6b7280",
              }}
            >
              Available Letters on File
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <button
                onClick={() => setStep("step1")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  border: "1.5px solid #e2ddd6",
                  background: "transparent",
                  color: "#6b7280",
                }}
              >
                <RotateCcw size={11} />
                Start Over
              </button>
              <div style={{ width: 1, height: 18, background: "#e2ddd6", flexShrink: 0 }} />
              {["all", "grant", "reinstatement", "name-change", "transfer"].map((f) => (
                <button
                  key={f}
                  onClick={() => setLetterTypeFilter(f)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    border: `1.5px solid ${letterTypeFilter === f ? "#1B2B4B" : "#e2ddd6"}`,
                    background: letterTypeFilter === f ? "#1B2B4B" : "transparent",
                    color: letterTypeFilter === f ? "white" : "#6b7280",
                  }}
                >
                  {f === "all"
                    ? "All"
                    : f === "grant"
                      ? "Grant"
                      : f === "reinstatement"
                        ? "Reinstatement"
                        : f === "name-change"
                          ? "Name Change"
                          : "Transfer"}
                </button>
              ))}
            </div>
          </div>

          {/* Letter table */}
          {helixLoading ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <Loader2
                size={32}
                style={{ animation: "spin 0.8s linear infinite", color: "#1B2B4B", margin: "0 auto" }}
              />
              <p style={{ color: "#6b7280", marginTop: 12, fontSize: 14 }}>
                Loading available letters…
              </p>
            </div>
          ) : filteredLetters.length === 0 ? (
            <div style={{ padding: "24px 0", color: "#6b7280", textAlign: "center", fontSize: 14 }}>
              No letters found for {docketPrefix}-{docketNumber}.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", marginBottom: 4, fontSize: 14 }}
              >
                <thead>
                  <tr>
                    {["", "Letter Type", "Authority", "Cargo", "Service Date", "Price"].map(
                      (h, i) => (
                        <th
                          key={i}
                          style={{
                            padding: "10px 12px",
                            borderBottom: "1.5px solid #e2ddd6",
                            textAlign: "left",
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: "#6b7280",
                            whiteSpace: "nowrap",
                            ...(i === 0 ? { width: 40 } : {}),
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredLetters.map((l) => {
                    const selected = selectedLetterIds.has(l.id);
                    const archive = isArchiveLetter(l.service_date);
                    const price = archive ? archiveRate : recentRate;
                    const cargoInfo =
                      (l.letter_code ? getCargoShortInfo(l.letter_code) : null) ??
                      (l.authority_type ? getCargoShortInfo(l.authority_type) : null);
                    return (
                      <tr
                        key={l.id}
                        onClick={() => toggleLetter(l.id)}
                        style={{
                          background: selected ? "#f0ede8" : undefined,
                          cursor: "pointer",
                        }}
                      >
                        <td style={{ padding: "14px 12px", borderBottom: "1px solid #e2ddd6" }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleLetter(l.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: 17, height: 17, accentColor: "#1B2B4B" }}
                          />
                        </td>
                        <td
                          style={{
                            padding: "14px 12px",
                            borderBottom: "1px solid #e2ddd6",
                            fontWeight: 600,
                          }}
                        >
                          {l.letter_type ?? "Authority Letter"}
                          {archive && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 10,
                                fontWeight: 600,
                                background: "#fef3c7",
                                color: "#d97706",
                                padding: "1px 6px",
                                borderRadius: 10,
                              }}
                            >
                              Archive
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "14px 12px",
                            borderBottom: "1px solid #e2ddd6",
                            fontSize: 13,
                            color: "#6b7280",
                          }}
                        >
                          {cargoInfo?.authorityLabel ?? "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "14px 12px",
                            borderBottom: "1px solid #e2ddd6",
                            fontSize: 13,
                            color: "#6b7280",
                          }}
                        >
                          {cargoInfo?.cargoLabel ?? "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "14px 12px",
                            borderBottom: "1px solid #e2ddd6",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {l.service_date
                            ? parseDateOnly(l.service_date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td
                          style={{
                            padding: "14px 12px",
                            borderBottom: "1px solid #e2ddd6",
                            fontWeight: 700,
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ${price.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Watchlist / Not Yet Issued Card */}
          <div
            style={{
              border: "1.5px solid #c4b5fd",
              borderRadius: 14,
              marginTop: 20,
              background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "18px 24px 16px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#5b21b6" }}>
                  Request Letters Not Yet Issued
                </div>
                <div style={{ fontSize: 13, color: "#6d28d9", marginTop: 4 }}>
                  Monitor this carrier and be notified when new authority letters are issued. NOTE: Changes by the FMCSA are impacting our ability to obtain letters served after May 21, 2026. We're working on it as a top priority. You will not be charged for letters we are unable to obtain.
                </div>
              </div>
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: "#6d28d9",
                  borderRadius: 10,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Bell size={18} color="white" />
              </div>
            </div>

            <div style={{ height: 1, background: "#e9d5ff", margin: "0 24px" }} />

            <div style={{ padding: "14px 24px 20px" }}>
              {/* FMCSA-derived pending items */}
              {fmcsaPendingItems.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#6d28d9",
                      marginBottom: 10,
                    }}
                  >
                    Pending FMCSA Applications — select to add to your request
                  </div>
                  {fmcsaPendingItems.map((item, idx) => (
                    <label
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        padding: "9px 12px",
                        background: selectedPendingIndexes.has(idx)
                          ? "rgba(124,58,237,0.08)"
                          : "rgba(196,181,253,0.12)",
                        border: `1px solid ${selectedPendingIndexes.has(idx) ? "#c4b5fd" : "rgba(196,181,253,0.5)"}`,
                        borderRadius: 8,
                        marginBottom: 7,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPendingIndexes.has(idx)}
                        onChange={() => togglePendingItem(idx)}
                        style={{ width: 15, height: 15, accentColor: "#6d28d9", marginTop: 2, flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#5b21b6" }}>
                          {item.letter_type} — Pending with FMCSA
                        </div>
                        {item.authority_type_code && (
                          <div style={{ fontSize: 11, color: "#6d28d9", marginTop: 2 }}>
                            {item.authority_type_code.split("—").slice(1).join("—").trim() || item.authority_type_code}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </>
              )}

              {/* Not in list toggle */}
              <div
                style={{
                  height: 1,
                  background: "#e9d5ff",
                  margin: fmcsaPendingItems.length > 0 ? "14px 0 10px" : "8px 0 10px",
                }}
              />
              <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    flexShrink: 0,
                    marginTop: 2,
                    background: notInListChecked ? "#6d28d9" : "transparent",
                    border: `2px solid ${notInListChecked ? "#6d28d9" : "#c4b5fd"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => setNotInListChecked((v) => !v)}
                >
                  {notInListChecked && (
                    <span style={{ color: "white", fontSize: 11, lineHeight: 1 }}>✓</span>
                  )}
                </div>
                <div onClick={() => setNotInListChecked((v) => !v)}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>
                    Request a letter not in this list
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>
                    Describe the letter type, authority, and cargo you need
                  </div>
                </div>
              </label>

              {notInListChecked && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1.5px solid #e9d5ff" }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    Letter Type *
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    {["Authority Grant", "Reinstatement", "Name Change", "Transfer"].map((lt) => (
                      <button
                        key={lt}
                        onClick={() => {
                          setNyiLetterType(nyiLetterType === lt ? "" : lt);
                          setNyiAuthorityType("");
                          setNyiCargoType("");
                        }}
                        style={{
                          padding: "9px 14px",
                          borderRadius: 10,
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: "pointer",
                          border: `1.5px solid ${nyiLetterType === lt ? "#1B2B4B" : "#e2ddd6"}`,
                          background: nyiLetterType === lt ? "#1B2B4B" : "white",
                          color: nyiLetterType === lt ? "white" : "#1a1a1a",
                        }}
                      >
                        {lt}
                      </button>
                    ))}
                  </div>

                  {nyiLetterType &&
                    !["Name Change", "Transfer"].includes(nyiLetterType) &&
                    docketPrefix !== "FF" && (
                      <>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "#6b7280",
                            marginBottom: 8,
                          }}
                        >
                          Authority Type{" "}
                          <span
                            style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 11 }}
                          >
                            (optional)
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                          {["Carrier", "Broker"].map((at) => (
                            <button
                              key={at}
                              onClick={() => setNyiAuthorityType(nyiAuthorityType === at ? "" : at)}
                              style={{
                                flex: 1,
                                padding: "9px 12px",
                                borderRadius: 10,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: "pointer",
                                border: `1.5px solid ${nyiAuthorityType === at ? "#1B2B4B" : "#e2ddd6"}`,
                                background: nyiAuthorityType === at ? "#1B2B4B" : "white",
                                color: nyiAuthorityType === at ? "white" : "#1a1a1a",
                              }}
                            >
                              {at}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                  {nyiLetterType && !["Name Change", "Transfer"].includes(nyiLetterType) && (
                    <>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "#6b7280",
                          marginBottom: 8,
                        }}
                      >
                        Cargo Type{" "}
                        <span
                          style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 11 }}
                        >
                          (optional)
                        </span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {["Property", "Household Goods", "Passengers"].map((ct) => (
                          <button
                            key={ct}
                            onClick={() => setNyiCargoType(nyiCargoType === ct ? "" : ct)}
                            style={{
                              flex: 1,
                              padding: "9px 14px",
                              borderRadius: 10,
                              fontSize: 13,
                              fontWeight: 500,
                              cursor: "pointer",
                              border: `1.5px solid ${nyiCargoType === ct ? "#1B2B4B" : "#e2ddd6"}`,
                              background: nyiCargoType === ct ? "#1B2B4B" : "white",
                              color: nyiCargoType === ct ? "white" : "#1a1a1a",
                            }}
                          >
                            {ct}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pricing Box */}
          {hasSelection && (
            <div
              style={{
                background: "#F7F4EE",
                border: "1.5px solid #e2ddd6",
                borderRadius: 12,
                padding: "20px 24px",
                margin: "20px 0",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#6b7280",
                  marginBottom: 14,
                }}
              >
                Pricing Summary
              </div>
              {recentLetters.length > 0 && (
                <div
                  style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "5px 0" }}
                >
                  <span style={{ color: "#6b7280" }}>
                    {recentLetters.length} × Authority Letter @ ${recentRate.toFixed(2)}
                  </span>
                  <span style={{ fontWeight: 600 }}>${recentCost.toFixed(2)}</span>
                </div>
              )}
              {archiveLetters.length > 0 && (
                <div
                  style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "5px 0" }}
                >
                  <span style={{ color: "#6b7280" }}>
                    {archiveLetters.length} × Archive Letter @ ${archiveRate.toFixed(2)}
                  </span>
                  <span style={{ fontWeight: 600 }}>${archiveCost.toFixed(2)}</span>
                </div>
              )}
              {selectedWatchItems.length > 0 && (
                <div
                  style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "5px 0" }}
                >
                  <span style={{ color: "#6b7280" }}>
                    {selectedWatchItems.length} × Watch Request @ ${watchRate.toFixed(2)}
                  </span>
                  <span style={{ fontWeight: 600 }}>${watchCost.toFixed(2)}</span>
                </div>
              )}
              <hr style={{ border: "none", borderTop: "1.5px solid #e2ddd6", margin: "10px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1B2B4B",
                  paddingTop: 4,
                }}
              >
                <span>Estimated Total</span>
                <span>${totalCost.toFixed(2)}</span>
              </div>
              {billingPreference === "batch" && (
                <div style={{ fontSize: 13, color: "#6b7280", fontStyle: "italic", marginTop: 8 }}>
                  You will be invoiced per your billing agreement.
                </div>
              )}
            </div>
          )}

          {/* Additional Contact */}
          <div
            style={{
              border: "1.5px solid #e2ddd6",
              borderRadius: 12,
              padding: "20px 24px",
              marginTop: 20,
              background: "#fafaf9",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#6b7280",
                marginBottom: 14,
              }}
            >
              Send Letters To Additional Contact{" "}
              <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                (optional)
              </span>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                type="email"
                value={additionalEmail}
                onChange={(e) => {
                  setAdditionalEmail(e.target.value);
                  setAdditionalEmailAutofilled(false);
                }}
                placeholder="Enter email address…"
                aria-invalid={additionalEmailIsRequester}
                aria-describedby={additionalEmailIsRequester ? "additional-email-error" : undefined}
                data-testid="input-additional-email"
                style={{
                  flex: 1,
                  minWidth: 200,
                  padding: "11px 14px",
                  fontSize: 14,
                  color: "#1a1a1a",
                  background: additionalEmailIsRequester
                    ? "#fef2f2"
                    : additionalEmailAutofilled
                      ? "#fffbeb"
                      : "white",
                  border: additionalEmailIsRequester
                    ? "1.5px solid #dc2626"
                    : additionalEmailAutofilled
                      ? "1.5px solid #d97706"
                      : "1.5px solid #e2ddd6",
                  borderRadius: 8,
                  outline: "none",
                  transition: "border-color 0.15s, background 0.15s",
                }}
              />
              {fmcsaRecord?.email_address && (
                <button
                  onClick={() => {
                    setAdditionalEmail(fmcsaRecord.email_address ?? "");
                    setAdditionalEmailAutofilled(true);
                  }}
                  style={{
                    padding: "9px 16px",
                    fontSize: 13,
                    fontWeight: 500,
                    border: "1.5px solid #d97706",
                    borderRadius: 8,
                    cursor: "pointer",
                    background: "#fffbeb",
                    color: "#92400e",
                    whiteSpace: "nowrap",
                  }}
                >
                  Use FMCSA Primary Contact
                </button>
              )}
            </div>
            {additionalEmailIsRequester && (
              <div
                id="additional-email-error"
                role="alert"
                data-testid="text-additional-email-error"
                style={{ fontSize: 12, color: "#dc2626", marginTop: 5 }}
              >
                This is already your email — you'll receive the letters automatically. Enter a
                different address or leave this blank.
              </div>
            )}
            {additionalEmailAutofilled && docketPrefix && docketNumber && (
              <p style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                Auto-filled from FMCSA carrier record for {docketPrefix}-{docketNumber}
              </p>
            )}
            {additionalEmail && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input
                  type="text"
                  value={additionalFirstName}
                  onChange={(e) => setAdditionalFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  style={{
                    flex: 1,
                    padding: "9px 12px",
                    fontSize: 13,
                    color: "#1a1a1a",
                    background: "white",
                    border: "1.5px solid #e2ddd6",
                    borderRadius: 8,
                    outline: "none",
                  }}
                />
                <input
                  type="text"
                  value={additionalLastName}
                  onChange={(e) => setAdditionalLastName(e.target.value)}
                  placeholder="Last name (optional)"
                  style={{
                    flex: 1,
                    padding: "9px 12px",
                    fontSize: 13,
                    color: "#1a1a1a",
                    background: "white",
                    border: "1.5px solid #e2ddd6",
                    borderRadius: 8,
                    outline: "none",
                  }}
                />
              </div>
            )}
          </div>

          {/* Stripe PaymentElement */}
          {isPayAtService && hasSelection && !nyiIncomplete && showStripeForm && (
            <div
              style={{
                border: "1.5px solid #e2ddd6",
                borderRadius: 12,
                padding: 24,
                margin: "20px 0",
                background: "#fafaf9",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#6b7280",
                  marginBottom: 16,
                }}
              >
                Payment — charged upon submission
              </div>
              <StripePaymentElement onComplete={setElementComplete} />
            </div>
          )}
          {isPayAtService && hasSelection && !nyiIncomplete && showStripeLoading && (
            <div
              style={{
                marginTop: 16,
                padding: 16,
                background: "#fef3c7",
                borderRadius: 10,
                fontSize: 14,
                color: "#92400e",
                textAlign: "center",
              }}
            >
              Loading payment form…
            </div>
          )}

          {/* Unified Submit Request button for all billing types */}
          <GoldButton
            onClick={isPayAtService ? handlePayAtServiceSubmit : handleBatchSubmit}
            disabled={submitDisabled}
            style={{ marginTop: 16 }}
          >
            Submit Request
          </GoldButton>
        </CardWrap>
      </PortalPage>
      </div>
    ) : null;

    const step4Jsx =
      step === "step4" && isPayAtService ? (
        <>
          <Step4StripeConfirm
            onSuccess={(piId) => {
              setActivePaymentIntentId(piId);
              submitMutation.mutate(piId);
            }}
            onError={(msg) => {
              setErrorMsg(msg);
              setStep("step3");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
          <PortalPage>
            <PortalHero eyebrow="Authority Letter Requests" title="Request An Authority Letter" />
            <CardWrap maxWidth={560}>
              <StepIndicator current={4} />
              <div style={{ textAlign: "center", padding: "48px 24px" }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    border: "3px solid #e2ddd6",
                    borderTopColor: "#1B2B4B",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                    margin: "0 auto 20px",
                  }}
                />
                <h3
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: 22,
                    color: "#1B2B4B",
                    marginBottom: 8,
                  }}
                >
                  Processing Your Request
                </h3>
                <p style={{ color: "#6b7280", fontSize: 14 }}>
                  Confirming payment and creating work orders…
                  <br />
                  Please don't close or refresh this page.
                </p>
              </div>
            </CardWrap>
          </PortalPage>
        </>
      ) : null;

    if (stripeReady) {
      return (
        <Elements stripe={stripePromise!} options={{ clientSecret: clientSecret! }} key={clientSecret!}>
          {step3Jsx}
          {step4Jsx}
        </Elements>
      );
    }
    // Batch billing or Stripe not yet ready: render step3 directly
    return step3Jsx;
  }

  if (step === "step5" && submitState) {
    const isPartial = submitState.status === "partial_failure";
    const capped = submitState.selectedLetters.length > MAX_DOWNLOAD_ALL;

    const resetForm = () => {
      setStep("step1");
      setDocketRaw("");
      setDocketPrefix(null);
      setDocketNumber("");
      setFmcsaRecord(null);
      setHelixLetters([]);
      setSelectedLetterIds(new Set());
      setSelectedPendingIndexes(new Set());
      setNotInListChecked(false);
      setNyiLetterType("");
      setNyiAuthorityType("");
      setNyiCargoType("");
      setAdditionalEmail("");
      setAdditionalFirstName("");
      setAdditionalLastName("");
      setAdditionalEmailAutofilled(false);
      setErrorMsg(null);
      setElementComplete(false);
      setClientSecret(null);
      setPrevPaymentIntentId(null);
      setActivePaymentIntentId(null);
      setSubmitState(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
      <PortalPage onLogout={onLogout}>
        <PortalHero eyebrow="Authority Letter Requests" title="Request An Authority Letter" />
        <CardWrap maxWidth={780}>
          <StepIndicator current={5} />

          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div
              style={{
                width: 60,
                height: 60,
                background: isPartial ? "#fef3c7" : "#dcfce7",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
                fontSize: 28,
              }}
            >
              {isPartial ? "⚠️" : "✅"}
            </div>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 26,
                fontWeight: 700,
                color: "#1B2B4B",
                marginBottom: 6,
              }}
            >
              {isPartial ? "Request Partially Completed" : "Request Submitted Successfully"}
            </h2>
            <div
              style={{
                display: "inline-block",
                background: "#1B2B4B",
                color: "white",
                fontSize: 13,
                fontWeight: 600,
                padding: "5px 14px",
                borderRadius: 20,
                marginTop: 10,
              }}
            >
              {submitState.carrierName} · {submitState.docket}
            </div>
          </div>

          {/* Partial failure notice */}
          {isPartial && submitState.partialFailures.length > 0 && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fde68a",
                borderRadius: 10,
                padding: "16px 20px",
                fontSize: 14,
                color: "#92400e",
                marginBottom: 20,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={16} /> Some work orders could not be created
              </div>
              {submitState.partialFailures.map((f, i) => (
                <div key={i} style={{ fontSize: 13, marginTop: 4 }}>
                  • {f.serviceType}: {f.error ?? "Unknown error"}
                </div>
              ))}
              {submitState.paymentIntentId && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#78350f" }}>
                  Some work orders could not be created. Your full payment was captured. Contact support with reference{" "}
                  <strong>{submitState.paymentIntentId}</strong>{" "}and we'll resolve this for you.
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 12, color: "#78350f" }}>
                Contact support: <strong>{PORTAL_PHONE}</strong> or{" "}
                <strong>{PORTAL_EMAIL}</strong>
              </div>
            </div>
          )}

          {/* Work Orders Table */}
          {submitState.workOrderIds.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#6b7280",
                  marginBottom: 12,
                }}
              >
                Work Orders Created
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                  marginBottom: 20,
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr>
                    {["Work Order #", "Service", "Watchlist", "Amount"].map((h) => (
                      <th
                        key={h}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "#6b7280",
                          padding: "10px 14px",
                          borderBottom: "1.5px solid #e2ddd6",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {submitState.workOrderIds.map((id) => {
                    const detail = submitState.workOrderDetails?.find((d) => d.workOrderId === id);
                    const amountCents = detail?.totalAmountCents ?? null;
                    const serviceType = detail?.serviceType ?? null;
                    return (
                      <tr key={id}>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: "1px solid #e2ddd6",
                            fontWeight: 700,
                            color: "#1B2B4B",
                          }}
                        >
                          {detail?.workOrderNumber ?? `#${id}`}
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: "1px solid #e2ddd6",
                            color: "#374151",
                          }}
                        >
                          {detail?.serviceTypeName ?? serviceType ?? "Authority Letter"}
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: "1px solid #e2ddd6",
                            color: "#374151",
                            textAlign: "center",
                          }}
                        >
                          {detail?.isWatchlist ? "✓" : ""}
                        </td>
                        <td
                          style={{
                            padding: "13px 14px",
                            borderBottom: "1px solid #e2ddd6",
                            color: "#374151",
                          }}
                        >
                          {amountCents != null
                            ? `$${(amountCents / 100).toFixed(2)}`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* Email sent notice — always shown */}
          <div
            style={{
              background: "#dcfce7",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
              padding: "14px 18px",
              fontSize: 14,
              color: "#16a34a",
              fontWeight: 500,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            ✉️{" "}
            {portalUser.email ? (
              <>
                Letters sent to <strong>{portalUser.email}</strong>
                {submitState.additionalEmail && (
                  <> and <strong>{submitState.additionalEmail}</strong></>
                )}
              </>
            ) : submitState.additionalEmail ? (
              <>
                Letters sent to <strong>{submitState.additionalEmail}</strong>
              </>
            ) : (
              "Letter confirmation emails have been sent."
            )}
          </div>

          {/* Watchlist notice */}
          {submitState.hasWatchlist && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fde68a",
                borderRadius: 10,
                padding: "14px 18px",
                fontSize: 14,
                color: "#92400e",
                fontWeight: 500,
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              🔔 Monitoring {submitState.docket} — you'll be notified when new letters are issued.
            </div>
          )}

          {/* Download section */}
          {submitState.selectedLetters.length > 0 && (
            <div style={{ borderTop: "1.5px solid #e2ddd6", paddingTop: 24, marginTop: 24 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <h4
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#6b7280",
                  }}
                >
                  Download Your Letters
                </h4>
                {submitState.selectedLetters.length > 1 && (
                  <button
                    onClick={downloadAll}
                    style={{
                      background: "#C9962A",
                      color: "white",
                      border: "none",
                      padding: "7px 14px",
                      borderRadius: 7,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <Download size={13} />
                    Download All (
                    {Math.min(submitState.selectedLetters.length, MAX_DOWNLOAD_ALL)}
                    {capped ? ` of ${submitState.selectedLetters.length}` : ""})
                  </button>
                )}
              </div>
              {capped && (
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                  Showing {MAX_DOWNLOAD_ALL} of {submitState.selectedLetters.length} letters.
                  Contact support for the remaining files.
                </div>
              )}
              {submitState.selectedLetters.slice(0, MAX_DOWNLOAD_ALL).map((l) => {
                const cargoInfo = l.letter_code ? getCargoShortInfo(l.letter_code) : null;
                const displayName = cargoInfo
                  ? `${l.letter_type ?? "Authority Letter"} — ${cargoInfo.authorityLabel}${cargoInfo.cargoLabel ? ` · ${cargoInfo.cargoLabel}` : ""}${l.letter_code ? ` (${l.letter_code})` : ""}`
                  : (l.letter_type ?? "Authority Letter");
                const archive = isArchiveLetter(l.service_date);
                return (
                  <div
                    key={l.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 16px",
                      border: "1.5px solid #e2ddd6",
                      borderRadius: 10,
                      marginBottom: 8,
                      cursor: "pointer",
                    }}
                    onClick={() => downloadLetter(l.id)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: "#1B2B4B", fontSize: 14 }}>
                        {displayName}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        {submitState.docket}
                        {l.service_date &&
                          ` · ${parseDateOnly(l.service_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                        {archive && " · Archive"}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadLetter(l.id);
                      }}
                      style={{
                        background: "#1B2B4B",
                        color: "white",
                        border: "none",
                        padding: "7px 14px",
                        borderRadius: 7,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <Download size={13} />
                      PDF
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={resetForm}
            style={{
              marginTop: 24,
              width: "100%",
              padding: "15px 24px",
              background: "transparent",
              color: "#1B2B4B",
              border: "1.5px solid #e2ddd6",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Submit Another Request
          </button>
        </CardWrap>
      </PortalPage>
    );
  }

  return (
    <PortalPage onLogout={onLogout}>
      <div
        style={{
          minHeight: "calc(100vh - 63px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 480 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 26,
              fontWeight: 700,
              color: "#1B2B4B",
              marginBottom: 12,
            }}
          >
            Something Went Wrong
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
            {errorMsg ?? "An unexpected error occurred. Please contact our support team."}
          </p>
          <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
            📞 {PORTAL_PHONE} · 📧 {PORTAL_EMAIL}
          </div>
          <GoldButton
            onClick={() => {
              setStep("step1");
              setErrorMsg(null);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            style={{ width: "auto", display: "inline-flex" }}
          >
            Try Again
          </GoldButton>
        </div>
      </div>
    </PortalPage>
  );
}

// ─── Public export — guarded by PortalGuard ────────────────────────────────────
export default function PortalRequestPage() {
  return (
    <PortalGuard>
      {({ onLogout, portalUser }) => (
        <PortalRequestPageInner onLogout={onLogout} portalUser={portalUser} />
      )}
    </PortalGuard>
  );
}
